import React, { useState, useEffect, useCallback } from 'react';
import { 
  Building, Search, Filter, MapPin, Globe, Users, Award, Star,
  Eye, Share2, ExternalLink, Mail, Phone, Calendar, Download,
  CheckCircle, Crown, Target, Leaf, Heart, Shield, TrendingUp,
  Briefcase, Clock, ArrowUpRight, Bookmark, Flag, Loader2, RefreshCw, AlertCircle
} from 'lucide-react';

// Constants
const industries = [
  'All Industries',
  'Clean Technology',
  'Financial Services',
  'Education',
  'Healthcare',
  'Manufacturing',
  'Retail & Consumer',
  'Professional Services',
  'Non-Profit',
  'Food & Beverage'
];

const tierConfig = {
  'Luminary': { color: 'bg-yellow-100 text-yellow-800', icon: Crown },
  'Inspired': { color: 'bg-blue-100 text-blue-800', icon: Star },
  'Aspiring': { color: 'bg-green-100 text-green-800', icon: Target }
};

const pillarLabels = {
  leadership: { name: 'Conscious Leadership', icon: Shield, color: 'blue' },
  culture: { name: 'Human-Centric Culture & Team Cohesion', icon: Heart, color: 'green' },
  products: { name: 'Products & Services', icon: Target, color: 'purple' },
  customers: { name: 'Customers & Clients', icon: Users, color: 'orange' },
  community: { name: 'Community & Conscious Social Impact', icon: Globe, color: 'teal' },
  brand: { name: 'Brand Vision & Collaboration', icon: Building, color: 'red' }
};

// Helper function - moved outside component
const getTierBadge = (tier, cisScore) => {
  const config = tierConfig[tier];
  const Icon = config?.icon || Award;

  return (
    <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium ${config?.color || 'bg-gray-100 text-gray-800'}`}>
      <Icon className="h-4 w-4" />
      <span>{tier}</span>
      {cisScore && <span className="text-xs opacity-75">({cisScore})</span>}
    </div>
  );
};

// OrganizationCard component - moved outside Directory
const OrganizationCard = ({ org, isFeatured = false, onViewProfile }) => {
  // Normalize field names from API
  const name = org.public_name || org.name;
  const cisScore = org.cis_score || org.cisScore;
  const logoUrl = org.logo_url || org.logo;
  const contact = org.contact || {};
  const visibility = org.visibility || {};
  const pillarScores = org.pillar_scores || org.pillars || {};
  const impactMetrics = org.impact_metrics || org.impact || {};
  const socialProof = org.social_proof || org.socialProof || {};
  
  return (
    <div className={`bg-white rounded-lg border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-lg ${
      isFeatured ? 'ring-2 ring-yellow-400 ring-opacity-50' : ''
    }`}>
      {isFeatured && (
        <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-2">
          <div className="flex items-center space-x-2 text-sm font-medium">
            <Star className="h-4 w-4" />
            <span>Featured Organization</span>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="p-6">
        <div className="flex items-start space-x-4">
          <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
            {logoUrl ? (
              <img src={logoUrl} alt={`${name} logo`} className="w-full h-full object-cover" />
            ) : (
              <Building className="h-8 w-8 text-gray-400" />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{name}</h3>
                <p className="text-sm text-blue-600 font-medium mb-2">{org.tagline}</p>
              </div>
              {getTierBadge(org.tier, cisScore)}
            </div>
            <p className="text-sm text-gray-600 line-clamp-2">{org.description}</p>
          </div>
        </div>
      </div>

      {/* Quick Info */}
      <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center text-gray-600">
            <Briefcase className="h-4 w-4 mr-2 text-gray-400" />
            {org.industry}
          </div>
          <div className="flex items-center text-gray-600">
            <MapPin className="h-4 w-4 mr-2 text-gray-400" />
            {org.location || 'Location not specified'}
          </div>
          {visibility.show_team_size && org.size && (
            <div className="flex items-center text-gray-600">
              <Users className="h-4 w-4 mr-2 text-gray-400" />
              {org.size}
            </div>
          )}
          {org.founded && (
            <div className="flex items-center text-gray-600">
              <Calendar className="h-4 w-4 mr-2 text-gray-400" />
              Founded {org.founded}
            </div>
          )}
        </div>
      </div>

      {/* CIS Score Bar */}
      {cisScore && visibility.show_certification && (
        <div className="px-6 py-4 border-t border-gray-100">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-medium text-gray-700">Conscious Impact Score</span>
            <span className="font-bold text-blue-600">{cisScore}/100</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${cisScore}%` }}
            />
          </div>
        </div>
      )}

      {/* Social Proof */}
      {socialProof && (socialProof.awards?.length > 0 || socialProof.partnerships > 0) && (
        <div className="px-6 py-3 border-t border-gray-100 bg-gray-50">
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            {socialProof.awards?.length > 0 && (
              <div className="flex items-center space-x-1">
                <Award className="h-4 w-4 text-yellow-500" />
                <span>{socialProof.awards.length} Awards</span>
              </div>
            )}
            {socialProof.partnerships > 0 && (
              <div className="flex items-center space-x-1">
                <Building className="h-4 w-4 text-blue-500" />
                <span>{socialProof.partnerships} Partners</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {visibility.allow_direct_contact && contact.email && (
            <a 
              href={`mailto:${contact.email}`}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
              title="Contact"
            >
              <Mail className="h-5 w-5" />
            </a>
          )}
          <button className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Save">
            <Bookmark className="h-5 w-5" />
          </button>
          <button className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Share">
            <Share2 className="h-5 w-5" />
          </button>
        </div>
        <button
          onClick={() => onViewProfile(org)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
        >
          View Profile
        </button>
      </div>
    </div>
  );
};

// OrganizationDetailModal component - moved outside Directory
const OrganizationDetailModal = ({ org, onClose }) => {
  if (!org) return null;
  
  const name = org.public_name || org.name;
  const cisScore = org.cis_score || org.cisScore;
  const logoUrl = org.logo_url || org.logo;
  const contact = org.contact || {};
  const visibility = org.visibility || {};
  const pillarScores = org.pillar_scores || org.pillars || {};
  const impactMetrics = org.impact_metrics || org.impact || {};
  const highlights = org.highlights || [];
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-lg">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-20 h-20 bg-white rounded-lg flex items-center justify-center overflow-hidden">
                {logoUrl ? (
                  <img src={logoUrl} alt={name} className="w-full h-full object-cover" />
                ) : (
                  <Building className="h-10 w-10 text-gray-400" />
                )}
              </div>
              <div>
                <h2 className="text-2xl font-bold">{name}</h2>
                <p className="text-blue-100">{org.tagline}</p>
                <div className="flex items-center space-x-3 mt-2">
                  {getTierBadge(org.tier, cisScore)}
                  <span className="text-sm text-blue-100">{org.industry}</span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="text-white hover:text-blue-200">✕</button>
          </div>
        </div>
        
        <div className="p-6">
          {/* Description */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">About</h3>
            <p className="text-gray-600">{org.description}</p>
          </div>
          
          {/* Contact Info */}
          {visibility.show_contact && (contact.email || contact.phone || contact.website) && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {contact.email && (
                  <a href={`mailto:${contact.email}`} className="flex items-center text-blue-600 hover:underline">
                    <Mail className="h-4 w-4 mr-2" />
                    {contact.email}
                  </a>
                )}
                {contact.phone && (
                  <a href={`tel:${contact.phone}`} className="flex items-center text-gray-600">
                    <Phone className="h-4 w-4 mr-2" />
                    {contact.phone}
                  </a>
                )}
                {contact.website && (
                  <a href={contact.website} target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-600 hover:underline">
                    <Globe className="h-4 w-4 mr-2" />
                    Website
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                )}
              </div>
            </div>
          )}
          
          {/* Pillar Scores */}
          {visibility.show_certification && pillarScores && Object.keys(pillarScores).length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Pillar Performance</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(pillarScores).map(([key, score]) => {
                  const pillar = pillarLabels[key];
                  if (!pillar) return null;
                  const Icon = pillar.icon;
                  return (
                    <div key={key} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <Icon className="h-4 w-4 text-gray-500" />
                        <span className="text-xs font-medium text-gray-600">{pillar.name}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${score}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-gray-700">{score}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Highlights */}
          {highlights.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Highlights</h3>
              <div className="space-y-2">
                {highlights.map((highlight, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">{highlight}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Impact Metrics */}
          {visibility.show_impact_metrics && impactMetrics && Object.keys(impactMetrics).length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Impact Metrics</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {impactMetrics.co2_reduced && (
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <Leaf className="h-6 w-6 text-green-600 mx-auto mb-1" />
                    <p className="text-lg font-bold text-green-700">{impactMetrics.co2_reduced}</p>
                    <p className="text-xs text-green-600">CO₂ Reduced</p>
                  </div>
                )}
                {impactMetrics.people_impacted && (
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <Users className="h-6 w-6 text-blue-600 mx-auto mb-1" />
                    <p className="text-lg font-bold text-blue-700">{impactMetrics.people_impacted}</p>
                    <p className="text-xs text-blue-600">People Impacted</p>
                  </div>
                )}
                {impactMetrics.community_investment && (
                  <div className="p-3 bg-purple-50 rounded-lg text-center">
                    <Heart className="h-6 w-6 text-purple-600 mx-auto mb-1" />
                    <p className="text-lg font-bold text-purple-700">{impactMetrics.community_investment}</p>
                    <p className="text-xs text-purple-600">Community Investment</p>
                  </div>
                )}
                {impactMetrics.jobs_created && (
                  <div className="p-3 bg-orange-50 rounded-lg text-center">
                    <Briefcase className="h-6 w-6 text-orange-600 mx-auto mb-1" />
                    <p className="text-lg font-bold text-orange-700">{impactMetrics.jobs_created}</p>
                    <p className="text-xs text-orange-600">Jobs Created</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Specializations */}
          {org.specializations && org.specializations.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Specializations</h3>
              <div className="flex flex-wrap gap-2">
                {org.specializations.map((spec, index) => (
                  <span key={index} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                    {spec}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex items-center space-x-3 pt-4 border-t">
            {visibility.allow_direct_contact && contact.email && (
              <a 
                href={`mailto:${contact.email}`}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Contact Organization
              </a>
            )}
            {visibility.allow_partnerships && (
              <button className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors">
                Partnership Inquiry
              </button>
            )}
            <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
              <Share2 className="h-4 w-4 inline mr-2" />
              Share
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Directory = () => {
  const [selectedTier, setSelectedTier] = useState('all');
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [organizations, setOrganizations] = useState([]);
  const [featuredOrgs, setFeaturedOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [stats, setStats] = useState(null);
  const [sortBy, setSortBy] = useState('recent');
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch directory listings from API
  const fetchDirectory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      let url = `${BACKEND_URL}/api/directory?sort=${sortBy}`;
      
      if (selectedTier !== 'all') {
        url += `&tier=${encodeURIComponent(selectedTier)}`;
      }
      if (selectedIndustry !== 'all' && selectedIndustry !== 'All Industries') {
        url += `&industry=${encodeURIComponent(selectedIndustry)}`;
      }
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch directory');
      
      const data = await response.json();
      setOrganizations(data.listings || []);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching directory:', err);
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL, sortBy, selectedTier, selectedIndustry, searchQuery]);

  // Fetch featured organizations
  const fetchFeatured = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/directory/featured`);
      if (response.ok) {
        const data = await response.json();
        setFeaturedOrgs(data.listings || []);
      }
    } catch (err) {
      console.error('Error fetching featured:', err);
    }
  }, [BACKEND_URL]);

  // Fetch statistics
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/directory/stats/summary`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [BACKEND_URL]);

  useEffect(() => {
    fetchDirectory();
    fetchFeatured();
    fetchStats();
  }, [fetchDirectory, fetchFeatured, fetchStats]);

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDirectory();
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, fetchDirectory]);

  // Track profile view
  const handleViewProfile = async (org) => {
    setSelectedOrg(org);
    setShowDetailModal(true);
    
    try {
      await fetch(`${BACKEND_URL}/api/directory/${org.id}/view`, {
        method: 'POST'
      });
    } catch (err) {
      console.error('Error tracking view:', err);
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Recognition Directory</h1>
            <p className="text-gray-700 mt-2">
              Discover certified conscious businesses committed to ethical practices
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{stats?.total_listings || organizations.length}</div>
              <div className="text-sm text-gray-500">Organizations</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{stats?.luminary_count || 0}</div>
              <div className="text-sm text-gray-500">Luminary</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{stats?.featured_count || featuredOrgs.length}</div>
              <div className="text-sm text-gray-500">Featured</div>
            </div>
          </div>
        </div>
      </div>

      {/* Information Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-white" />
            </div>
            <h3 className="font-bold text-blue-900">CNESS Assessment</h3>
          </div>
          <p className="text-sm text-blue-800">
            Complete the assessment to evaluate your organization across 6 pillars. Earn certification and join this directory.
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-50 to-fuchsia-50 border border-purple-100 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <h3 className="font-bold text-purple-900">Mentorship Program</h3>
          </div>
          <p className="text-sm text-purple-800">
            Connect with experienced mentors who guide you on your conscious business journey. Get personalized support.
          </p>
        </div>
        
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-amber-600 rounded-lg flex items-center justify-center">
              <Award className="w-5 h-5 text-white" />
            </div>
            <h3 className="font-bold text-amber-900">Get Listed</h3>
          </div>
          <p className="text-sm text-amber-800">
            Achieve certification to get listed in this directory. Increase visibility, build trust, and attract conscious customers.
          </p>
        </div>
      </div>

      {/* Featured Organizations */}
      {featuredOrgs.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Featured Organizations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredOrgs.slice(0, 3).map((org) => (
              <OrganizationCard key={org.id} org={org} isFeatured={true} onViewProfile={handleViewProfile} />
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg p-4 border border-gray-200">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search organizations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <select 
            value={selectedTier}
            onChange={(e) => setSelectedTier(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">All Tiers</option>
            <option value="Luminary">Luminary</option>
            <option value="Inspired">Inspired</option>
            <option value="Aspiring">Aspiring</option>
          </select>
          
          <select 
            value={selectedIndustry}
            onChange={(e) => setSelectedIndustry(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            {industries.map((industry) => (
              <option key={industry} value={industry}>{industry}</option>
            ))}
          </select>
          
          <select 
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="recent">Most Recent</option>
            <option value="cis_score">Highest Score</option>
            <option value="name">Alphabetical</option>
            <option value="partners">Most Partners</option>
          </select>
          
          <button 
            onClick={fetchDirectory}
            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Results */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          {loading ? 'Loading...' : `${organizations.length} Organizations Found`}
        </h3>
        <div className="flex items-center space-x-2">
          <button 
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-500'}`}
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          </button>
          <button 
            onClick={() => setViewMode('list')}
            className={`p-2 rounded ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-500'}`}
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : error ? (
        <div className="text-center py-12 bg-red-50 rounded-lg">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-600">{error}</p>
          <button onClick={fetchDirectory} className="mt-4 text-blue-600 hover:underline">
            Try Again
          </button>
        </div>
      ) : organizations.length > 0 ? (
        <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
          {organizations.map((org) => (
            <OrganizationCard key={org.id} org={org} isFeatured={false} onViewProfile={handleViewProfile} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No organizations found matching your criteria.</p>
          <button 
            onClick={() => {
              setSelectedTier('all');
              setSelectedIndustry('all');
              setSearchQuery('');
            }}
            className="mt-4 text-blue-600 hover:underline"
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedOrg && (
        <OrganizationDetailModal 
          org={selectedOrg} 
          onClose={() => {
            setShowDetailModal(false);
            setSelectedOrg(null);
          }} 
        />
      )}
    </div>
  );
};

export default Directory;
